/**
 * Lexiun Browser pane discoverability nudge: surface a toast when
 * the agent invokes the upstream OpenClaw `browser` tool with
 * `action: "navigate"` while the user is NOT already on the Lexiun
 * Browser pane.
 *
 * Structural twin of `useFileOpenFromAgent` — both subscribe to the
 * gateway's `session.tool` broadcasts and react to the `phase: "start"`
 * variant of a specific tool. The agent uses the NATIVE upstream
 * `browser` tool (extensions/browser/, single tool with 17 sub-actions
 * via the `action` parameter); the Lexiun side just allows the call
 * through the conditional GATE + makes it CDP-attach to the
 * supervisor's Chromium. There is no Lexiun-specific browser tool —
 * the earlier `lx_browser_navigate` slice was a misread and removed
 * 2026-06-01.
 *
 * Match condition: `toolName === "browser"` AND
 * `toolInput?.action === "navigate"`. Other sub-actions (click, type,
 * snapshot, …) are deliberately NOT toasted — a navigate is the only
 * action whose result is a new page worth pulling the user's gaze
 * toward. The toast is suppressed when
 * `useUIStore.activeMode === "agentBrowser"` (no point nudging the
 * user toward a pane they're already on).
 *
 * Mount site: `CaseWorkspaceLayout` + `GlobalConversationLayout`
 * (alongside `useFileOpenFromAgent`).
 */

import { useEventStream } from "@/hooks/useEventStream";
import { useToastStore } from "@/stores/useToastStore";
import { useUIStore } from "@/stores/useUIStore";

interface AgentEventPayloadRuntime {
  runId?: string;
  seq?: number;
  stream?: string;
  ts?: number;
  sessionKey?: string;
  data?: {
    phase?: string;
    toolName?: string;
    toolInput?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

function shortenForToast(url: string): string {
  // Keep the toast text short — strip the trailing path beyond the host
  // for anything over 60 chars total. Leaves the protocol + host fully
  // visible so the user can recognize where the agent went.
  if (url.length <= 60) return url;
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}/...`;
  } catch {
    return `${url.slice(0, 57)}...`;
  }
}

export function useBrowserActivityFromAgent(): void {
  const addToast = useToastStore((s) => s.addToast);

  useEventStream("session.tool", (payload) => {
    const typed = payload as AgentEventPayloadRuntime;
    if (typed.data?.phase !== "start") return;
    if (typed.data?.toolName !== "browser") return;
    // The native browser tool is a single tool with 17 sub-actions;
    // we only nudge on "navigate" since that's the action whose result
    // is a new page worth surfacing to the user.
    const action = typed.data?.toolInput?.action;
    if (action !== "navigate") return;

    // Read at fire time — subscribing to the store via a hook here
    // would re-run the effect on every activeMode change and rebind
    // the event stream. The store's getState() is the right API for
    // a fire-and-forget filter.
    const activeMode = useUIStore.getState().activeMode;
    if (activeMode === "agentBrowser") return;

    const rawUrl = typed.data?.toolInput?.url;
    const url =
      typeof rawUrl === "string" && rawUrl.length > 0
        ? shortenForToast(rawUrl)
        : "(browser)";

    addToast({
      text: `Agent is navigating to ${url}`,
      kind: "info",
      autoDismissMs: 6000,
    });
  });
}
